import { FC } from 'react';
import { Alert, Box, Stack, TextField, Typography } from '@mui/material';
import moment from 'moment';
import { api } from '../../helpers/apiUtils';
import { Feld } from '../../components/marketingForm';
import { KENNZEICHEN_LIVE } from './texteLive';

/**
 * Newsletter-Schlagzeile der Live-Anzeige („Livestream-Ad Titel“), gebunden an EINEN Sendetag.
 *
 * Paket 2 (28.10.2026): bis dahin eine eigene Karte im Reiter „Heute“ mit eigenem Datumsfeld. Jetzt
 * sitzt sie im Termin-Dialog des Sendeplans — der Sendetag ist der Tag des Dialogs, und gespeichert
 * wird zusammen mit dem Termin (Schedule.tsx). Diese Datei liefert nur das Feld samt Vorschau und
 * die beiden Aufrufe; den Zustand hält der Dialog.
 *
 * Leer = die Anzeige zeigt die Betreffzeile der Newsletter-Ausgabe (Standard, Server-Rückfall in
 * `LiveScheduleService.getNewsletterLiveNotice`).
 */

/** Der Newsletter-Cron versendet um 08:54 Berlin; spätere Änderungen verpassen die Ausgabe des Tages. */
const SEND_HOUR = 8;
const SEND_MINUTE = 54;
export const NEWSLETTER_VERSAND = `${String(SEND_HOUR).padStart(2, '0')}:${String(SEND_MINUTE).padStart(2, '0')}`;

export interface SchlagzeileStand {
  headline: string | null;
  maxLength: number;
}

/** Liest die gespeicherte Schlagzeile eines Sendetags. Wirft bei Fehler (Aufrufer setzt den Prefill-Riegel). */
export async function ladeSchlagzeile(datum: string): Promise<SchlagzeileStand> {
  const res = await api.get<{
    success: boolean;
    data: { headline: string | null; maxLength: number };
  }>('/api/live/admin/ad-headline', { params: { date: datum } });
  return {
    headline: res.data?.data?.headline ?? null,
    maxLength: res.data?.data?.maxLength || 200,
  };
}

/** Speichert die Schlagzeile; gibt zurück, was der Server tatsächlich behalten hat. */
export async function speichereSchlagzeile(datum: string, text: string): Promise<string | null> {
  const res = await api.put<{ success: boolean; data: { headline: string | null } }>(
    '/api/live/admin/ad-headline',
    { date: datum, headline: text },
  );
  return res.data?.data?.headline ?? null;
}

const WOCHENTAGE = [
  'Sonntag',
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
];

/** `2026-10-29` → „Donnerstag, 29.10.“ — lokale Teile, kein UTC. */
function sendetagText(datum: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(datum);
  if (!m) return datum;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return `${WOCHENTAGE[d.getDay()]}, ${m[3]}.${m[2]}.`;
}

/**
 * Vorschau der Live-Zeile im Newsletter — Nachbau des Prod-Templates „minimalistic“
 * (`node-app/src/helpers/newsletterBodyVariants.ts`, `renderLiveNotice`, Zweig mit `headline`):
 *   - Kennzeichen „● LIVE“ (rot) oder bei einer Aufzeichnung „AUFZEICHNUNG“ (grau),
 *   - Metazeile „HEUTE <Zeit> UHR · KURSGESPRÄCH MIT <MODERATION>“ — „HEUTE“ steht dort immer, weil der
 *     Newsletter am Sendetag selbst verschickt wird; die Vorschau nennt deshalb den Tag der Ausgabe dazu,
 *   - Themen stehen in diesem Template NICHT in der Anzeige.
 * Farben aus `newsletterDesignTokens.ts` (negative / textMuted / brandPrimary).
 */
const NL_ROT = '#b53030';
const NL_GRAU = '#5a6470';
const NL_BLAU = '#1956a8';
const NL_KNOPF = 'Hier klicken zur heutigen Liveshow';

const AdHeadline: FC<{
  /** Sendetag `YYYY-MM-DD` (der Tag des Termin-Dialogs). */
  datum: string;
  wert: string;
  maxLength: number;
  /** false, solange der gespeicherte Stand nicht geladen ist — dann nicht editierbar. */
  geladen: boolean;
  onChange: (wert: string) => void;
  /** Startzeit `HH:MM` aus dem Dialog (steht im Newsletter in der Metazeile). */
  zeit: string;
  /** Anzeigename der Moderation, null = noch niemand gewählt (dann ohne „MIT …“). */
  moderatorName: string | null;
  /** true = zur Sendezeit läuft eine Aufzeichnung — der Newsletter schreibt dann „AUFZEICHNUNG“. */
  istAufzeichnung: boolean;
  /** Die Knöpfe unten im Dialog, die speichern (wörtlich, z. B. „Nur speichern“ oder „Speichern & bestätigen“). */
  speichernKnoepfe: string;
}> = ({
  datum,
  wert,
  maxLength,
  geladen,
  onChange,
  zeit,
  moderatorName,
  istAufzeichnung,
  speichernKnoepfe,
}) => {
  const heute = moment().format('YYYY-MM-DD');
  const vorbei =
    datum < heute ||
    (datum === heute && moment().isAfter(moment().hour(SEND_HOUR).minute(SEND_MINUTE).second(0)));
  const zeitKurz = (zeit || '').slice(0, 5) || '—';
  const meta = `HEUTE ${zeitKurz} UHR · KURSGESPRÄCH${
    moderatorName?.trim() ? ` MIT ${moderatorName.trim().toLocaleUpperCase('de-DE')}` : ''
  }`;

  return (
    <Feld
      titel="Schlagzeile im Newsletter"
      art="empfohlen"
      kennzeichenErklaerung={KENNZEICHEN_LIVE.empfohlen}
      wofuer={
        <>
          Die große Zeile in der Live-Anzeige des Newsletters vom Sendetag (Versand um{' '}
          {NEWSLETTER_VERSAND} Uhr). Die Anzeige erscheint nur, wenn die Sendung bis dahin bestätigt
          ist. Leer lassen — dann steht dort die Betreffzeile der Newsletter-Ausgabe.
        </>
      }
    >
      {vorbei && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Der Newsletter dieses Tages ist schon verschickt (Versand {NEWSLETTER_VERSAND} Uhr). Eine
          Änderung hier wirkt nicht mehr.
        </Alert>
      )}
      <TextField
        fullWidth
        size="small"
        placeholder="z. B. Nvidia bricht aus — worauf es jetzt ankommt"
        value={wert}
        disabled={!geladen}
        onChange={(e) => onChange(e.target.value.slice(0, maxLength))}
        helperText={
          geladen
            ? `${wert.length}/${maxLength} Zeichen — wird mit ${speichernKnoepfe} übernommen`
            : 'Wird geladen …'
        }
      />
      {/* Vorschau — so steht die Anzeige im Newsletter. Ohne sie tippt die Redaktion blind. */}
      <Box
        sx={{
          mt: 1.5,
          p: 1.5,
          border: 1,
          borderColor: 'divider',
          bgcolor: '#fafafa',
        }}
      >
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary', letterSpacing: '.06em' }}
        >
          VORSCHAU — SO STEHT ES IM NEWSLETTER VOM {sendetagText(datum).toLocaleUpperCase('de-DE')}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
          Der Newsletter geht am Sendetag selbst raus — „Heute“ meint dort also den{' '}
          {sendetagText(datum)}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75, flexWrap: 'wrap' }}>
          <Box
            sx={{
              bgcolor: istAufzeichnung ? NL_GRAU : NL_ROT,
              color: '#fff',
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '.08em',
              px: 0.9,
              py: 0.3,
            }}
          >
            {istAufzeichnung ? 'AUFZEICHNUNG' : '● LIVE'}
          </Box>
          <Typography
            sx={{ fontSize: 12, fontWeight: 700, color: NL_GRAU, letterSpacing: '.04em' }}
          >
            {meta}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 20, lineHeight: 1.25, fontWeight: 800 }}>
          {wert.trim() || (
            <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
              Betreffzeile der Newsletter-Ausgabe
            </Box>
          )}
        </Typography>
        <Box
          sx={{
            display: 'inline-block',
            mt: 1,
            bgcolor: NL_BLAU,
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            px: 2,
            py: 1,
          }}
        >
          {NL_KNOPF}
        </Box>
      </Box>
    </Feld>
  );
};

export default AdHeadline;
